<template>
  <aside :class="[
    'fixed left-0 top-16 h-screen bg-white shadow-sm transition-all duration-300',
    props.collapsed ? 'w-16' : 'w-60',
  ]">
    <nav class="py-4">
      <div v-for="(menuItem, index) in menuItems" :key="index" class="mb-2">
        <div class="relative" @mouseenter="showSubmenu = menuItem.key" @mouseleave="showSubmenu = ''">
          <button @click="handleMenuClick(menuItem)" :class="[
            'w-full flex items-center px-4 py-3 hover:bg-gray-50 transition-colors',
            activeMenu === menuItem.key
              ? 'bg-blue-50 text-blue-600'
              : 'text-gray-600',
            props.collapsed ? 'justify-center' : 'justify-between',
          ]">
            <div class="flex items-center space-x-3">
              <i :class="menuItem.icon"></i>
              <span v-if="!props.collapsed" class="transition-opacity duration-300">{{ menuItem.name }}</span>
            </div>
            <i v-if="!props.collapsed && menuItem.children"
              class="fas fa-chevron-right text-xs transition-transform duration-300"></i>
          </button>
          <div v-if="menuItem.children && showSubmenu === menuItem.key" :class="[
            'absolute bg-white shadow-lg rounded-lg py-2 z-[9999] submenu-scroll',
            props.collapsed
              ? 'left-full top-0 ml-1'
              : 'left-full top-0',
            ['finance', 'settings'].includes(menuItem.key)
              ? 'max-h-[200px] overflow-y-auto'
              : 'max-h-[calc(100vh-4rem)] overflow-y-auto'
          ]" style="min-width: 180px">
            <div v-for="child in menuItem.children" :key="child.key" v-show="shouldShowMenuItem(child.key)" :class="[
              'w-full flex items-center justify-between px-4 py-2 text-sm hover:bg-gray-50 transition-colors group',
              activeSubMenu === child.key ? 'text-blue-600' : 'text-gray-600',
            ]">
              <button @click="handleSubMenuClick(child)" class="flex-1 text-left">
                {{ child.name }}
              </button>
              <button v-if="getAddPath(child.key)" @click="handleAddClick(child.key)"
                class="ml-2 px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                title="新增">
                <span>新增</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  </aside>
</template>

<script setup>
import { ref, defineProps } from "vue";
import { useRouter } from "vue-router";
import { shouldShowMenuItem } from "@/utils/auth";

const props = defineProps({
  collapsed: {
    type: Boolean,
    default: false,
  },
});
const activeMenu = ref("");
const showSubmenu = ref("");
const activeSubMenu = ref("");
const router = useRouter();

const handleMenuClick = (menuItem) => {
  activeMenu.value = menuItem.key;
  if (!menuItem.children) {
    router.push(menuItem.path || `/${menuItem.key}`);
  }
};

const handleSubMenuClick = (child) => {
  activeSubMenu.value = child.key;
  if (child.externalUrl) {
    window.location.href = child.externalUrl;
    return;
  }
  if (child.path) {
    router.push(child.path);
  } else {
    router.push(`/${child.key}`);
  }
};

const handleAddClick = (childKey) => {
  const addPath = getAddPath(childKey);
  if (addPath) {
    router.push(addPath);
  }
};

// 根据子菜单key获取对应的新增页面路径
const getAddPath = (childKey) => {
  const addPathMap = {
    'SalesListOrder': '/Sales/SalesAdd',
    'SalesReturnList': '/Sales/SalesReturnAdd',
    //'customer': '/Sales/CustomerAdd',
    //'CustomerPrice': '/CustomerPrice',
    'PurchaseListOrder': '/Purchase/PurchaseAdd',
    'PurchaseReturnList': '/Purchase/PurchaseReturnAdd',
    //'SupplierList': '/Purchase/SupplierAdd',
    'InventoryCheckList': '/inventory/InventoryCheckAdd',
    'AssemblyList': '/inventory/AssemblyAdd',
    'OtherTransfer': '/inventory/OtherTransferAdd',
    'ReceiptList': '/Finance/ReceiptAdd',
    'PaymentList': '/Finance/PaymentAdd',
    'WriteOffVoucherList': '/Finance/WriteOffVoucherCreate',
    'IncomeExpenseList': '/Finance/IncomeExpenseAdd',
    "ProductManagement": "/settings/ProductCreate",
    "WarehouseManagement": "/settings/warehouse/add",
    "PaymentApprovalManagement": "/approval-v2/payment/add",
    "InvoiceApprovalManagement": "/approval-v2/invoice/add",
    "SealApprovalManagement": "/approval-v2/seal/add",
    //'BankAccountList': '/Finance/BankAccountAdd',
    //'UserManagement': '/settings/UserAdd'
  };
  return addPathMap[childKey] || null;
};
const menuItems = ref([
  { name: "仪表盘", icon: "fas fa-chart-line", key: "dashboard" },
  {
    name: "销售管理",
    icon: "fas fa-cash-register",
    key: "sales",
    children: [
      { name: "销售单管理", key: "SalesListOrder", path: "/Sales/SalesListOrder" },
      { name: "销售退货单管理", key: "SalesReturnList", path: "/Sales/SalesReturnList" },
      //shao
      { name: "商城订单管理", key: "ShopOrderList", path: "/ShopOrders/OrderList" },
      { name: "客户管理", key: "customer", path: "/Sales/Customers" },
      // { name: "一客一价管理", key: "CustomerPriceIndex", path: "/CustomerPrice/CustomerPriceIndex" },
      // { name: "销售报表", key: "SalesReport",path:"/Sales/SalesReport" },
    ],
  },
  {
    name: "进货管理",
    icon: "fas fa-shopping-cart",
    key: "purchase",
    children: [
      { name: "进货单管理", key: "PurchaseListOrder", path: "/Purchase/PurchaseListOrder" },
      { name: "进货退货单管理", key: "PurchaseReturnList", path: "/Purchase/PurchaseReturnList" },
      { name: "供应商管理", key: "SupplierList", path: "/Purchase/SupplierList" },
      // { name: "进货报表", key: "PurchaseReport", path: "/Purchase/PurchaseReport" },
    ],
  },
  {
    name: "库存管理",
    icon: "fas fa-warehouse",
    key: "inventory",
    children: [
      { name: "库存查询", key: "InventoryList", path: "/inventory/InventoryList" },
      { name: "库存盘点", key: "InventoryCheckList", path: "/inventory/InventoryCheckList" },
      // { name: "组装拆装", key: "AssemblyList", path: "/inventory/AssemblyList" },
      // { name: "其他出入库", key: "OtherTransfer", path: "/inventory/OtherTransferList" },
    ],
  },
  {
    name: "财务管理",
    icon: "fas fa-money-bill-wave",
    key: "finance",
    children: [
      { name: "应收账款", key: "ReceivableList", path: "/Finance/ReceivableList" },
      { name: "收款单管理", key: "ReceiptList", path: "/Finance/ReceiptList" },
      { name: "应付账款", key: "PayableList", path: "/Finance/PayableList" },
      { name: "付款单管理", key: "PaymentList", path: "/Finance/PaymentList" },
      { name: "核销单管理", key: "WriteOffVoucherList", path: "/Finance/WriteOffVoucherList" },
      { name: "收支记录管理", key: "IncomeExpenseList", path: "/Finance/IncomeExpenseList" },
      // { name: "收支类型管理", key: "IncomeExpenseTypeList", path: "/Finance/IncomeExpenseTypeList" },
      { name: "资金流水", key: "fund-flows", path: "/finance/fund-flows" },
      { name: "结算账户管理", key: "BankAccountList", path: "/Finance/BankAccountList" },
      // { name: "销售开票(销售)", key: "SalesInvoiceList1",path:"/Finance/InvoiceApplicationListForSales" },
      { name: "销售开票", key: "SalesInvoiceList2", path: "/Finance/InvoiceApplicationList" },
    ],
  },
  {
    name: "报表管理",
    icon: "fas fa-chart-line",
    key: "reports",
    children: [
      { name: "销售报表", key: "SalesReport", path: "/Reports/SalesReport" },
      { name: "客户报表", key: "CustomerReport", path: "/Reports/CustomerReport" },
      { name: "公司财务报表", key: "company-finance", path: "/Reports/CompanyFinanceReport" },
      // { name: "员工提成报表", key: "employee-commission", path: "/reports/employee-commission" },
    ],
  },//shao
  {
    name: "审批管理",
    icon: "fas fa-tasks",
    key: "approval",
    children: [
      // { name: "我的待办", key: "MyPendingApprovals", path: "/approval/my-pending" },
      // { name: "我的已办", key: "MyCompletedApprovals", path: "/approval/my-completed" },
      // { name: "我的申请", key: "MyApplications", path: "/approval/my-applications" },
      { name: "付款申请管理", key: "PaymentApprovalManagement", path: "/approval/payment-management" },
      { name: "发票申请管理", key: "InvoiceApprovalManagement", path: "/approval/invoice-management" },
      { name: "印章申请管理", key: "SealApprovalManagement", path: "/approval/seal-management" },
      // { name: "邮件模板配置", key: "EmailTemplateConfig", path: "/approval/email-template-config" },
      
    ],
  },
  {
    name: "系统设置",
    icon: "fas fa-cog",
    key: "settings",
    children: [
      { name: "用户管理", key: "UserManagement", path: "/settings/UserManagement" },
      { name: "企业管理", key: "PlatformCompanyManagement", path: "/platform/companies" },
      //shao
      { name: "渠道商管理", key: "ChannelMerchantManagement", path: "/settings/ChannelMerchantManagement" },
      { name: "业务员管理", key: "SalesmanManagement", path: "/settings/salesman" },
      { name: "仓库管理", key: "WarehouseManagement", path: "/settings/warehouse" },
      // { name: "角色权限", key: "UserRoles", path: "/settings/roles" },
      // { name: "系统参数", key: "system-params" },
      //shao
      { name: "消息配置", key: "MessageConfig", path: "/settings/message-config" },
      // { name: "新增商品", key: "ProductCreate", path: "/settings/ProductCreate" },
      { name: "商品管理", key: "ProductManagement", path: "/settings/ProductManagement" },
      { name: "商品信息管理", key: "ProductInfoManagement", externalUrl: "https://test.nwbbg.com/productinfo/index.html" },
      { name: "数据授权", key: "DataAuthorization", path: "/settings/data-authorization" },
      //shao
      { name: "审批流程配置", key: "ApprovalWorkflowList", path: "/settings/approval-workflows" },
      { name: "系统配置管理", key: "ConfigManager", path: "/system/config" },
    ],
  },
]);
</script>

<style scoped>
.submenu-scroll {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.submenu-scroll::-webkit-scrollbar {
  width: 6px;
}

.submenu-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.submenu-scroll::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 9999px;
}

.submenu-scroll::-webkit-scrollbar-thumb:hover {
  background-color: #94a3b8;
}
</style>
